import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { style } from './stock-indication.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t, TranslateResult } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct, ProductModel } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'


export const stockIndicationStyles = ['success', 'warning', 'error', 'info'] as const
export type StockIndicationStyle = typeof stockIndicationStyles[number]

export const stockIndicationVariants = ['default', 'simple', 'minimal', 'solid', 'floating'] as const
export type StockIndicationVariant = typeof stockIndicationVariants[number]

export const stockIndicationCompositions = ['solid', 'bordered', 'simple', 'floating'] as const
export type StockIndicationComposition = typeof stockIndicationCompositions[number]

export const stockIndicationDataSources = ['attributes', 'pdp-product'] as const
export type StockIndicationDataSource = typeof stockIndicationDataSources[number]

export const stockIndicationHiddenFields = ['text'] as const
export type StockIndicationHiddenField = typeof stockIndicationHiddenFields[number]

interface StockStatusItem {
  state: string
  iconRole: string
  textFactory: () => TranslateResult
}

interface StockStatus {
  in: StockStatusItem
  out: StockStatusItem
  limited: StockStatusItem
}

interface BadgeConfig {
  stock: number
  warningAmount: number
  indicationStyle: string | undefined
  indicationIcon: string
  indicationText: string
  tooltip: string
}

const emptyTileProduct: InternalProduct = new ProductModel().parseToInternal()

@customElement('csc-stock-indication')
export class CSCStockIndication extends HiddenFieldsMixin(LitElement, stockIndicationHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Number })
  stock = 0

  @property({ type: Number, attribute: 'warning-amount' })
  warningAmount = 0

  @property({ type: String })
  variant: StockIndicationVariant = 'default'

  /** @deprecated Use `variant` instead. */
  @property({ type: String })
  composition?: StockIndicationComposition

  @property({ type: String, attribute: 'stock-text' })
  stockText: string = 'Out of stock'

  @property({ type: String, attribute: 'tooltip-text' })
  tooltipText = ''

  /**
   * When set, the component renders in "indication" mode: the badge style, icon and
   * text come from the backend stock indication rule instead of the stock thresholds.
   */
  @property({ type: String, attribute: 'indication-style' })
  indicationStyle?: StockIndicationStyle

  @property({ type: String, attribute: 'indication-icon' })
  indicationIcon = ''

  @property({ type: String, attribute: 'indication-text' })
  indicationText = ''

  @property({ type: String, attribute: 'data-source' })
  dataSource: StockIndicationDataSource = 'attributes'

  @consume({ context: productTileProductContext })
  @state()
  private tileProduct: InternalProduct = emptyTileProduct

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @state()
  private pdpProduct: PdpProduct | null = null

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) this.pdpProduct = product
    })
  }

  /** True when an ancestor csc-base-product-tile provides real product data */
  private get isInTile(): boolean {
    return this.tileProduct !== emptyTileProduct
  }

  protected override willUpdate(changedProperties: PropertyValues): void {
    super.willUpdate(changedProperties)
    if (changedProperties.has('composition') && this.composition) {
      console.warn('<csc-stock-indication> "composition" is deprecated, use "variant" instead.')
    }
    if (changedProperties.has('hiddenFields') && this.isHidden('text')) {
      console.warn('<csc-stock-indication> hidden-fields="text" is deprecated, use variant="minimal" instead.')
    }
  }

  private get hidesText(): boolean {
    return this.variant === 'minimal' || this.isHidden('text')
  }

  private get content(): StockStatus {
    return {
      in:  {
        state: 'success',
        iconRole: 'csc-stock-indication.in-stock',
        textFactory: () => t('in_stock', { ns: 'csc-stock-indication', default: 'In stock' })
      },
      out: {
        state: 'error',
        iconRole: 'csc-stock-indication.out-of-stock',
        textFactory: () => t('out_of_stock', { ns: 'csc-stock-indication', default: 'Out of stock' })
      },
      limited: {
        state: 'warning',
        iconRole: 'csc-stock-indication.limited-stock',
        textFactory: () => t('limited_stock', { ns: 'csc-stock-indication', default: 'Limited stock' })
      }
    }
  }

  private renderTooltip = (tooltip: string) => {
    return html`
      <cse-tooltip position="top">
        ${tooltip}
        <cse-icon
          slot="anchor"
          icon-role="csc-stock-indication.tooltip"
          size="md"
        ></cse-icon>
      </cse-tooltip>
    `
  }

  private get badgeComposition(): StockIndicationComposition {
    if (this.composition) return this.composition

    switch(this.variant){
      case 'minimal':
      case 'simple':
        return 'simple'
      case 'solid':
      case 'floating':
        return this.variant
      default:
        return 'bordered'
    }
  }

  private activeIndicationStyleOf(indicationStyle?: string): StockIndicationStyle | null {
    return stockIndicationStyles.includes(indicationStyle as StockIndicationStyle)
      ? indicationStyle as StockIndicationStyle
      : null
  }

  private renderBadge(cfg: BadgeConfig) {
    const indicationStyle = this.activeIndicationStyleOf(cfg.indicationStyle)

    let state: string
    let icon: unknown
    let text: unknown

    if (indicationStyle) {
      state = indicationStyle
      icon = cfg.indicationIcon ? html`<cse-icon slot="prefix" icon-name="${cfg.indicationIcon}" icon-style="solid"></cse-icon>` : nothing
      text = cfg.indicationText
    } else {
      const stockState = cfg.stock < 1 ? 'out' : (cfg.stock < cfg.warningAmount ? 'limited' : 'in')
      const status = this.content[stockState]
      state = status.state
      icon = html`<cse-icon slot="prefix" icon-role="${status.iconRole}"></cse-icon>`
      text = status.textFactory()
    }

    return html`
      <div class="csc-stock-indication-wrapper">
        <cse-badge
          class="${indicationStyle ? `indication-${indicationStyle}` : ''}"
          composition="${this.badgeComposition}"
          variant="${state}"
        >
          ${icon}
          ${this.hidesText ? '' : text}
        </cse-badge>
        ${cfg.tooltip ? this.renderTooltip(cfg.tooltip) : ''}
      </div>
      `
  }

  private renderStandaloneMode() {
    return this.renderBadge({
      stock: this.stock,
      warningAmount: this.warningAmount,
      indicationStyle: this.indicationStyle,
      indicationIcon: this.indicationIcon,
      indicationText: this.indicationText,
      tooltip: this.tooltipText,
    })
  }

  private renderTileMode() {
    const indication = this.tileProduct.stock.indication

    return this.renderBadge({
      stock: this.tileProduct.stock.amount,
      warningAmount: this.warningAmount,
      indicationStyle: indication?.style,
      indicationIcon: indication?.icon ?? '',
      indicationText: indication?.text ?? '',
      tooltip: indication?.tooltip ?? this.tileProduct.stock.altMessage,
    })
  }

  private renderPdpProductMode(product: PdpProduct) {
    const indication = product.stock.indication

    return this.renderBadge({
      stock: product.stock.amount ?? 0,
      warningAmount: this.warningAmount,
      indicationStyle: indication?.style,
      indicationIcon: indication?.icon ?? '',
      indicationText: indication?.text ?? '',
      tooltip: indication?.tooltip ?? this.tooltipText,
    })
  }

  override render() {
    if (this.isInTile) return this.renderTileMode()
    if (this.dataSource === 'pdp-product') {
      return this.pdpProduct ? this.renderPdpProductMode(this.pdpProduct) : nothing
    }
    return this.renderStandaloneMode()
  }
}

initializeIconsByRole({
  'csc-stock-indication.in-stock': { name: 'circle-check', style: 'solid' },
  'csc-stock-indication.limited-stock': { name: 'clock', style: 'solid' },
  'csc-stock-indication.out-of-stock': { name: 'circle-xmark', style: 'solid' },
  'csc-stock-indication.tooltip': { name: 'info-circle', style: 'regular' },
})

declare global {
  interface HTMLElementTagNameMap {
    'csc-stock-indication': CSCStockIndication
  }
}
